<template>
  <!-- Nothing switched on for this zone type: GREYED, not hidden. A hidden panel can't
       be discovered, and with one enabled zone fleet-wide this is the surface that
       tells a manager the capability exists at all.
       docs/reference/service_response_measurement.md §7 rule 3. -->
  <!-- Every zone of this type is tripwire-only, so there is nothing to switch on.
       Deliberately NOT the adoption prompt: telling a manager to enable something that
       has no control would send him hunting for a setting that does not exist (§2). -->
  <ChartPanel
    v-if="state === 'not_measurable'"
    class="srt-root"
:title="$t('service.response.titleType', { types: plural })"
    icon="bi-person-raised-hand"
    height="auto"
    :accent="accent"
    muted
  >
    <p class="srt-note">
      {{ $t('service.response.typeTripwire') }}
    </p>
    <p class="srt-note srt-note--dim">
      {{ $t('service.response.typeAsArea') }}
    </p>
    <ul v-if="notMeasurable.length" class="srt-list srt-list--dim">
      <li v-for="z in notMeasurable" :key="z.zone">{{ z.zone }}</li>
    </ul>
  </ChartPanel>

  <ChartPanel
    v-else-if="state === 'none_enabled'"
    class="srt-root"
:title="$t('service.response.titleType', { types: plural })"
    icon="bi-person-raised-hand"
    height="auto"
    :accent="accent"
    muted
  >
    <p class="srt-note">
      {{ $t('service.response.typeNoneEnabled') }}
    </p>
    <p class="srt-note srt-note--dim">
      {{ $t('service.response.typeSwitchOn') }}
    </p>
    <p v-if="notEnabled.length" class="srt-cover-head">{{ $t('service.response.couldBeMeasured') }}</p>
    <ul v-if="notEnabled.length" class="srt-list srt-list--dim">
      <li v-for="z in notEnabled" :key="z.zone">{{ z.zone }}</li>
    </ul>
  </ChartPanel>

  <!-- Some zones ARE switched on, but no nightly figure yet. Rendering nothing here
       swallowed the adoption lists too — so a store with one zone enabled showed LESS
       than a store with none, which is backwards. The lists are the primary surface
       while adoption is low (§18), so they stay; only the figures wait. -->
  <ChartPanel
    v-else-if="state === 'pending'"
    class="srt-root"
:title="$t('service.response.titleType', { types: plural })"
    icon="bi-person-raised-hand"
    height="auto"
    :accent="accent"
    muted
  >
    <p class="srt-note">
      {{ $t('service.response.typePending', { n: coverage.zonesEnabled }, coverage.zonesEnabled) }}
    </p>

    <div v-if="notEnabled.length || notMeasurable.length" class="srt-cover">
      <div v-if="notEnabled.length">
        <p class="srt-cover-head">
          {{ $t('service.response.moreCould', { n: notEnabled.length }, notEnabled.length) }}
        </p>
        <ul class="srt-list"><li v-for="z in notEnabled" :key="z.zone">{{ z.zone }}</li></ul>
        <p class="srt-cover-hint">{{ $t('service.response.switchOnEach') }}</p>
      </div>
      <div v-if="notMeasurable.length">
        <p class="srt-cover-head">
          {{ $t('service.response.cantMeasure', { n: notMeasurable.length }, notMeasurable.length) }}
        </p>
        <ul class="srt-list"><li v-for="z in notMeasurable" :key="z.zone">{{ z.zone }}</li></ul>
        <p class="srt-cover-hint">
          {{ $t('service.response.cantMeasureHint') }}
        </p>
      </div>
    </div>
  </ChartPanel>

  <div v-else-if="state === 'measured'" class="srt-root">
    <!-- 1 — the number. -->
    <ChartPanel
  :title="$t('service.response.titleType', { types: plural })"
      icon="bi-person-raised-hand"
      height="auto"
      :accent="accent"
    >
      <div class="srt-kpi">
        <span class="srt-kpi-num" :style="{ color: accent }">{{ totals.notServed }}</span>
        <span class="srt-kpi-word">{{ $t('service.response.notServed') }}</span>
        <Tooltip
          :text="$t('service.response.basisTip')"
          placement="bottom"
          align="start"
          :max-width="360"
        >
          <i class="bi bi-info-circle srt-info"></i>
        </Tooltip>
      </div>
      <p class="srt-kpi-label">
        {{ $t('service.response.typeKpiLabel', { worth: totals.worthApproaching, n: coverage.zonesEnabled, span: timeSpan.toLowerCase() }, coverage.zonesEnabled) }}
      </p>

      <p v-if="!comparable" class="srt-nocompare">{{ comparableReason }}</p>

      <!-- The adoption block. With one enabled zone fleet-wide (§18) this does more
           work than the number above it. Two lists, because the fixes differ: one is
           a switch, the other means re-drawing the zone as an area. -->
      <div v-if="notEnabled.length || notMeasurable.length" class="srt-cover">
        <div v-if="notEnabled.length">
          <p class="srt-cover-head">
            {{ $t('service.response.moreCould', { n: notEnabled.length }, notEnabled.length) }}
          </p>
          <ul class="srt-list"><li v-for="z in notEnabled" :key="z.zone">{{ z.zone }}</li></ul>
          <p class="srt-cover-hint">{{ $t('service.response.switchOnEach') }}</p>
        </div>
        <div v-if="notMeasurable.length">
          <p class="srt-cover-head">
            {{ $t('service.response.cantMeasure', { n: notMeasurable.length }, notMeasurable.length) }}
          </p>
          <ul class="srt-list"><li v-for="z in notMeasurable" :key="z.zone">{{ z.zone }}</li></ul>
          <p class="srt-cover-hint">
            {{ $t('service.response.cantMeasureHint') }}
          </p>
        </div>
      </div>
    </ChartPanel>

    <ChartGrid class="srt-pair">
    <!-- 2 — WHEN, consolidated across every measured zone of this type. This is the
         store-level question: not "which zone is worst" (that is below, and it is a
         zone-level question) but "when does my store fail to serve people", which is a
         rota decision. Pooling the zones also makes the heatmap denser as adoption
         grows — the opposite of the per-zone version's weakness. -->
    <ChartPanel
      :title="$t('service.response.whenTitleType', { types: plural })"
      icon="bi-grid-3x3-gap"
      :height="340"
      :accent="accent"
      :empty="!heatmap"
    >
      <HeatmapDetectionZone
        v-if="heatmap"
        :heatmapChartData="heatmap"
        :timeSpan="timeSpan"
        :detectionZoneTypeName="typeName"
        :seriesName="$t('service.response.seriesName')"
        compact
      />
    </ChartPanel>

    <!-- 3 — WHERE. Demoted below the heatmap: it answers the second half of the same
         staffing decision. A ranked list rather than a bar chart, because at two to
         nine zones the thing being read is the NAME, which sits badly on an axis. -->
    <ChartPanel
      :title="$t('service.response.whichTitle', { types: plural })"
      icon="bi-bar-chart-steps"
      height="auto"
      :accent="accent"
      :empty="!zones.length"
    >
      <ul class="srt-bars">
        <li v-for="z in zones" :key="z.zone">
          <span class="srt-bar-name">{{ z.zone }}</span>
          <span class="srt-bar-track">
            <span class="srt-bar-fill"
                  :style="{ width: barWidth(z.notServed), background: accent }"></span>
          </span>
          <span class="srt-bar-val">{{ z.notServed }}</span>
        </li>
      </ul>
    </ChartPanel>
    </ChartGrid>
  </div>
</template>

<script>
/**
 * Service response for ONE zone type, rendered inside that type's Explore section.
 *
 * Why per type and not per store: Explore is already sectioned by zone type, so this
 * inherits that structure instead of inventing a parallel one — and the types differ in
 * what service means (a product zone is an approach opportunity; a demo zone is closer
 * to an appointment). docs/reference/service_response_measurement.md §9.
 *
 * Three panels in a deliberate order — the number, then WHEN, then WHERE. When and where
 * together are a staffing decision; either alone is only a fact.
 *
 * Presentation only: the demo store feeds it a static JSON block, production will feed
 * it the nightly rollup, and neither needs a change here.
 */
import { defineComponent, computed } from 'vue'
import ChartGrid from '@/components/ui/ChartGrid.vue'
import ChartPanel from '@/components/ui/ChartPanel.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import HeatmapDetectionZone from '@/components/HeatmapDetectionZone.vue'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { zoneTypeLabel } from '@/i18n/zoneTypes'

export default defineComponent({
  name: 'ServiceResponseTypePanel',
  components: { ChartGrid, ChartPanel, Tooltip, HeatmapDetectionZone },
  props: {
    // One entry of the rollup's byType map. null renders nothing.
    data: { type: Object, default: null },
    timeSpan: { type: String, default: 'Last 30 days' },
    accent: { type: String, default: 'rgb(0, 79, 110)' },
  },
  setup(props) {
    const { t } = useI18n()
    const coverage = computed(() => props.data?.coverage ?? {})
    const zones = computed(() => props.data?.zones ?? [])
    const totals = computed(() => props.data?.totals ?? { notServed: 0, worthApproaching: 0 })
    const heatmap = computed(() => props.data?.heatmap ?? null)
    const typeName = computed(() => props.data?.typeName ?? 'Product Zone')
    // "Product Zone" -> "Product Zones" for panel titles.
    // Dutch: the shown type name + s ("Productzones").
    const plural = computed(() => (currentLanguage() === 'en'
      ? `${typeName.value}s` : `${zoneTypeLabel(typeName.value)}s`))
    const notEnabled = computed(() => coverage.value.zonesNotEnabled ?? [])
    const notMeasurable = computed(() => coverage.value.zonesNotMeasurable ?? [])
    const comparable = computed(() => coverage.value.comparable !== false)
    const comparableReason = computed(() =>
      coverage.value.comparableReason ||
      t('service.response.noCompare')
    )

    const state = computed(() => {
      const d = props.data
      if (!d) return 'pending'
      // Production sends an explicit state (a zone can be enabled but have no nightly
      // figure yet, which is 'pending' and must NOT render as "nothing measured").
      // The demo block has no state field, so fall back to inspecting the payload.
      if (d.state) return d.state
      return zones.value.length ? 'measured' : 'none_enabled'
    })

    const maxCount = computed(() => Math.max(1, ...zones.value.map(z => z.notServed || 0)))
    // Proportional to the busiest zone, floored so a zone with one customer still
    // shows a mark rather than nothing.
    const barWidth = (n) => `${Math.max(4, ((n || 0) / maxCount.value) * 100)}%`

    return {
      state, coverage, zones, totals, heatmap, typeName, plural,
      notEnabled, notMeasurable, comparable, comparableReason, barWidth,
    }
  },
})
</script>

<style scoped>
/* Matches SubPanel's own 18px, so this block sits in the section at the same rhythm
   as the panels above it rather than flush against them. */
.srt-root { margin-top: 18px; }

.srt-kpi { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.srt-kpi-num { font-size: 2.4rem; font-weight: 700; line-height: 1; }
.srt-kpi-word { font-size: 1.05rem; color: #212529; font-weight: 600; }
.srt-kpi-label { font-size: 0.8rem; color: #9aa1a9; margin: 4px 0 0; max-width: 68ch; }
.srt-info { color: #b9c0c7; font-size: 0.82rem; }
.srt-info:hover { color: #6b7280; }

.srt-nocompare { margin: 12px 0 0; font-size: 0.8rem; color: #6b7280; }

.srt-cover {
  margin-top: 16px; padding-top: 14px; border-top: 1px solid #eef3f7;
  display: flex; flex-wrap: wrap; gap: 26px;
}
.srt-cover-head { font-size: 0.86rem; color: #212529; font-weight: 600; margin: 0; }
.srt-cover-hint { font-size: 0.76rem; color: #9aa1a9; margin: 4px 0 0; }
.srt-list { list-style: none; padding: 0; margin: 6px 0 0; }
.srt-list li { font-size: 0.82rem; color: #6b7280; padding: 1px 0; }
.srt-list--dim li { color: #9aa1a9; }

.srt-bars { list-style: none; padding: 0; margin: 0; }
/* The name column shrinks with the panel — 12rem is right beside a heatmap on a wide
   screen and far too much on a phone. clamp() does it without a media query, and the
   ellipsis handles what is left. */
.srt-bars li {
  display: grid;
  grid-template-columns: minmax(0, clamp(5rem, 38%, 12rem)) 1fr 2.4rem;
  align-items: center; gap: 10px; margin-bottom: 8px;
}
.srt-bar-name {
  font-size: 0.86rem; color: #212529;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* Matches the page's own .barlist — 10px on #eef3f7, .85 fill. */
.srt-bar-track { background: #eef3f7; border-radius: 999px; height: 10px; }
.srt-bar-fill { display: block; height: 10px; border-radius: 999px; opacity: .85; }
.srt-bar-val {
  font-size: 0.86rem; font-weight: 600; color: #212529;
  text-align: right; font-variant-numeric: tabular-nums;
}

/* Both panels need real width for the heatmap's 11 hour columns to stay legible;
   below this they stack rather than squeeze. min(...,100%) in ChartGrid's own track
   sizing keeps that from ever forcing a horizontal scrollbar. */
.srt-pair { --chart-min: 420px; margin-top: 16px; }

.srt-note { font-size: 0.9rem; color: #6b7280; margin: 0; max-width: 62ch; }
.srt-note--dim { font-size: 0.82rem; color: #9aa1a9; margin-top: 6px; }
</style>
